import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import t from 'react-translate';
import { pick } from 'lodash';
import { css } from '@emotion/react';
import * as yup from 'yup';

// redux
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { RootState } from 'redux/schemas';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCurrentUser } from 'redux/selectors/users';
import { getSkillTagsFromTaggings } from 'redux/selectors/skills-feedback';

import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { DeepPartial } from 'utility-types';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import ActivityTitle from 'shared/components/activity/activity-title';
import ActivityStatus from 'shared/components/activity/activity-status';
import { LecturePageMode, LectureComponentProps } from 'lecture_pages/components';
import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import LectureComponentDeadline from 'components/lecture-component-deadline';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { black, gray3, gray7, hexToRgbaString } from 'styles/global_defaults/colors';
import ViewValidationError from 'quizzes/components/view-validation-error';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import ActivityPoints from 'shared/components/activity/activity-points';
import EditInstructionFormModal from './ai-config-modal/set-up-instructions/edit-instruction-form-modal';
import EditAiInstructionContext from './ai-config-modal/set-up-instructions/context';
import modalStyles from './ai-config-modal/styles';
import ConversationModalContent from './ai-conversation-modal';
import AiConversationContext,
{ AIRolePlayStatus,
  AudioRecordingStatus,
  Message,
  SessionData,
} from './ai-conversation-modal/context';
import SubmitSection from './submit-section';
import useAiRolePlaySubmissionPusherEvents from './hooks/use-ai-role-play-submission-pusher-events';
import useAiConversationHandlers from './hooks/use-ai-conversation-handlers';

type AiRolePlayLectureComponentType = LectureComponent<ComponentType.AI_ROLE_PLAY>;
const MODAL_WIDTH = 1080;

const AiRolePlayLectureComponent = (props: LectureComponentProps<ComponentType.AI_ROLE_PLAY>) => {
  const {
    mode,
    currentLecture,
    lectureComponent,
  } = props;

  const styles = css`
    .description-container {
      width: 100%;
      text-align: left;
      .text-container {
        span {
          vertical-align: top !important;
        }
      }
      .bs4-input-group-append {
        display: none;
      }
      textarea {
        padding: ${quarterSpacing}px !important;
        background-color: ${mode === LecturePageMode.EDIT ? gray7 : 'transparent'} !important;
        line-height: ${headerLineHeight}px;
        min-height: ${mode === LecturePageMode.EDIT ? '200px' : '0px'} !important;
      }

      .bs4-form-control {
        border: ${mode === LecturePageMode.EDIT ? `1px solid ${gray3}` : 'none'} !important;
        text-align: left;
        box-sizing: border-box;
        padding: 0;
        overflow: hidden;
      }
    }
  `;

  const conversationModalStyles = css`
    .close {
      top: 0;
      right: 0;
    }
    .bs4-modal-content {
      background-color: ${hexToRgbaString(black, 0.8)} !important;
    }
  `;

  const dispatch = useAppDispatch();
  const currentLectureId = currentLecture.id;

  const aiRolePlay = useSelector((state) => getAiRolePlay(state, lectureComponent.aiRolePlay));
  const submission = useSelector((state) => (aiRolePlay.submissionId ? state.models.aiRolePlaySubmissions[aiRolePlay.submissionId] : null));
  const { pointsConfigurationId } = aiRolePlay || {};
  const pointsConfiguration = useSelector((state) => (pointsConfigurationId ? state.models.pointsConfigurations[pointsConfigurationId] : null));
  const currentCourse = useSelector(getCurrentCourse);
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const skillTags = useSelector(state => getSkillTagsFromTaggings(state, lectureComponent.skillTaggings) ?? []);

  const [showEditAiInstruction, setShowEditAiInstruction] = useState(false);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [aiRolePlayStatus, setAiRolePlayStatus] = useState(AIRolePlayStatus.NOT_STARTED);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isRolePlayStarting, setIsRolePlayStarting] = useState(false);
  const [openingNarrative, setOpeningNarrative] = useState(submission?.openingNarrative || '');
  const [isAwaitingAiResponse, setIsAwaitingAiResponse] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [sessionData, setSessionData] = useState<SessionData>({
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
    languageLocale: null,
  });
  const [audioRecordingStatus, setAudioRecordingStatus] = useState(AudioRecordingStatus.NOT_STARTED);

  useEffect(() => {
    if (!showConversationModal) {
      setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
    }
  }, [showConversationModal]);

  const {
    handleChunk,
    handleError,
    handleStartConversation,
  } = useAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    messages,
    setMessages,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    openingNarrative,
    setOpeningNarrative,
    setIsAwaitingAiResponse,
  });

  const forwardOnModalCloseRef = useRef(null);

  const isCurrentCourseSelfPaced = currentCourse.isSelfPaced;
  const lectureComponentId = lectureComponent.id;
  const {
    expirationDate,
  } = aiRolePlay || {};
  const isHardDeadlineExpired = aiRolePlay?.hardDeadline && aiRolePlay?.expired;

  const [title, setTitle] = useState<string>(aiRolePlay?.title ?? '');

  const descriptionValidationSchema = yup.object().shape({
    description: yup.string().max(10000, t.VALIDATION.MAX_LENGTH('10000')),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      description: aiRolePlay?.description || '',
    },
    resolver: yupResolver(descriptionValidationSchema),
  });
  const { formState: { errors }, reset } = methods;

  // Sync form with Redux state when aiRolePlay.description changes (e.g., after update API)
  useEffect(() => {
    reset({ description: aiRolePlay?.description || '' });
  }, [aiRolePlay?.description, reset]);

  // Sync title state with Redux state when aiRolePlay.title changes (e.g., after update API)
  useEffect(() => {
    setTitle(aiRolePlay?.title || '');
  }, [aiRolePlay?.title]);

  const { sharedProps, setSharedProps } = useContext(BaseLectureComponentContext);
  const initialSharedProps = useRef(sharedProps).current;

  const lecturePageId = useSelector((state: RootState) => state.app.lecturePage.currentLectureId);
  const lecturePage = useSelector(state => state.models.lecturePages[lecturePageId]);
  const { anonymizedIdentifier } = useSelector(getCurrentUser);
  const courseId = currentCourse.id;

  useAiRolePlaySubmissionPusherEvents({
    anonymizedIdentifier,
    courseId,
    aiRolePlayId: aiRolePlay?.id,
    currentCatalogId,
    pointsReceived: aiRolePlay?.pointsReceived,
    skillTags,
  });

  const openEditAiInstruction = useCallback(() => {
    setShowEditAiInstruction(true);
  }, []);

  const saveComponent = useCallback((componentDataPatch: DeepPartial<AiRolePlayLectureComponentType['aiRolePlay']>) => {
    const componentDataUpdate = {
      catalogId: currentCatalogId,
      lecturePageId: currentLectureId,
      componentData: {
        id: lectureComponentId,
        type: lectureComponent.type,
        aiRolePlay: {
          ...pick(aiRolePlay, [
            'isTodo',
            'hardDeadline',
            'expirationDate',
          ]),
          ...componentDataPatch,
        },
      },
    };

    return dispatch(updateLectureComponent(componentDataUpdate));
  }, [
    dispatch,
    aiRolePlay,
    currentLectureId,
    currentCatalogId,
    lectureComponentId,
    lectureComponent.type,
  ]);

  useEffect(() => {
    const options: NvDropdownOption[] = [{
      type: 'text',
      callback: openEditAiInstruction,
      text: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_ROLE_PLAY(),
      tooltip: {
        enabled: !!lecturePage.isLinked,
        text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
        placement: 'left',
        offset: 20,
      },
    }];

    if (!isCurrentCourseSelfPaced) {
      options.unshift(
        { type: 'text',
          text: expirationDate ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.REMOVE_DEADLINE() : t.LECTURE_PAGES.COMPONENTS.DROPDOWN.ADD_DEADLINE(),
          callback: () => {
            saveComponent({
              expirationDate: expirationDate
                ? null
                : moment(currentLecture.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
            });
          },
        },
        {
          type: 'divider',
        },
      );
    }

    setSharedProps({
      ...initialSharedProps,
      extraOptions: {
        mode: 'prepend',
        renderOnMount: true,
        options,
      },
    });
  }, [
    saveComponent,
    setSharedProps,
    expirationDate,
    openEditAiInstruction,
    initialSharedProps,
    isCurrentCourseSelfPaced,
    currentLecture.releaseDate,
  ]);

  const handleTitleBlur = () => saveComponent({
    title,
  });
  const handleTitleInputChange = (newTitle: string) => setTitle(newTitle);

  const handleDescriptionBlur = (newDescription: string) => {
    if (mode !== LecturePageMode.EDIT || errors.description) {
      return;
    }
    saveComponent({
      description: newDescription,
    });
  };

  const showDescriptionMaxCharLimitError = () => errors.description?.type === 'max';

  const handleOpenRolePlay = () => {
    setShowConversationModal(true);
  };

  const resetConversation = useCallback(() => {
    // Abort any existing stream before resetting state
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      setMessages([]);
    }
    setShowConversationModal(false);
    setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
    setShowScenarioDetails(true);
    setSessionData({
      aiApiEndpoint: null,
      jwtToken: null,
      submissionId: null,
      expiresAt: null,
      languageLocale: null,
    });
    setIsTimeout(false);
    setIsAwaitingAiResponse(false);
    setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
  }, [aiRolePlayStatus]);

  /**
   * Exits the chat. Shows confirmation dialog if conversation is in progress,
   * unless skipConfirmation is true (used for timeout scenarios).
   */
  const handleExitChat = useCallback((skipConfirmation = false) => {
    if (!skipConfirmation && aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      dispatch(openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE(),
        confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.CONFIRM(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: resetConversation,
      }));
    } else {
      resetConversation();
    }
  }, [aiRolePlayStatus, dispatch, resetConversation]);

  return (
    <div
      css={styles}
      className='d-flex flex-column align-items-center pb-6'
    >
      <div className='mb-2'>
        <ActivityStatus icon='role-play' status={aiRolePlay.progress} />
      </div>
      <ActivityTitle
        status={aiRolePlay.progress}
        statusesTexts={{
          not_started: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.PRACTICE(),
          completed: t.FORM.SUBMITTED(),
        }}
      />
      <LectureComponentHeaderInput
        required
        value={title}
        editable={mode === LecturePageMode.EDIT}
        onBlur={handleTitleBlur}
        onChange={handleTitleInputChange}
      />
      {
        pointsConfiguration
          && !!pointsConfiguration.points
          && (
            <div
              className='activity-points mb-2'
            >
              <ActivityPoints
                pointsReceived={aiRolePlay.pointsReceived}
                totalPoints={pointsConfiguration.points}
              />
            </div>
          )
      }
      <div className={expirationDate ? 'mb-2' : ''}>
        <LectureComponentDeadline
          isEdit={mode === LecturePageMode.EDIT}
          catalogId={currentCatalogId}
          componentName='ai-role-play'
          lecturePageId={currentLecture.id}
          expirationDate={expirationDate}
          hasHardDeadlines={aiRolePlay?.hardDeadline}
          onHardDeadlineChange={(hardDeadline) => {
            saveComponent({ hardDeadline });
          }}
          onDeadlineChange={(date) => {
            saveComponent({ expirationDate: date?.toISOString() });
          }}
          isDeadlineEditable={mode === LecturePageMode.EDIT}
        />
      </div>
      {(aiRolePlay?.description || mode === LecturePageMode.EDIT) && (
        <FormProvider {...methods}>
          <NvTextArea
            readOnly={mode !== LecturePageMode.EDIT}
            name='description'
            className='description-container mt-4'
            withForm
            onBlur={(e) => handleDescriptionBlur(e.target.value)}
            placeholder={mode === LecturePageMode.EDIT ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.ADD_DESCRIPTION() : ''}
          />
          {showDescriptionMaxCharLimitError() && (
          <div className='d-flex justify-content-end w-100'>
            <ViewValidationError characterLimit='10,000' />
          </div>
          )}
        </FormProvider>
      )}
      <SubmitSection
        aiRolePlayId={aiRolePlay.id}
        handleOpenRolePlay={handleOpenRolePlay}
        mode={mode}
        messages={messages}
        setMessages={setMessages}
        setAiRolePlayStatus={setAiRolePlayStatus}
        setShowConversationModal={setShowConversationModal}
        isHardDeadlineExpired={isHardDeadlineExpired}
        setSessionData={setSessionData}
        setOpeningNarrative={setOpeningNarrative}
      />
      <NvModal
        show={showEditAiInstruction}
        width={MODAL_WIDTH}
        type={ModalType.DYNAMIC}
        onClose={() => {
          if (forwardOnModalCloseRef.current) {
            forwardOnModalCloseRef.current();
          }
        }}
        header={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_ROLE_PLAY()}
        body={(
          <div css={modalStyles}>
            <EditAiInstructionContext.Provider value={{
              showEditAiInstruction,
              setShowEditAiInstruction,
              forwardOnModalCloseRef,
              aiRolePlayId: aiRolePlay.id,
            }}
            >
              <EditInstructionFormModal
                saveComponent={saveComponent}
                lectureComponent={lectureComponent}
              />
            </EditAiInstructionContext.Provider>
          </div>

        )}
      />
      <NvModal
        show={showConversationModal}
        modalStyles={conversationModalStyles}
        width={MODAL_WIDTH}
        type={ModalType.NO_DIALOG}
        onClose={() => handleExitChat()}
        body={(
          <AiConversationContext.Provider
            value={{
              handleExitChat,
              aiRolePlayStatus,
              setAiRolePlayStatus,
              showScenarioDetails,
              setShowScenarioDetails,
              setShowConversationModal,
              aiRolePlayId: aiRolePlay.id,
              messages,
              setMessages,
              sessionData,
              setSessionData,
              isTimeout,
              setIsTimeout,
              abortControllerRef,
              isHardDeadlineExpired,
              isRolePlayStarting,
              setIsRolePlayStarting,
              openingNarrative,
              handleChunk,
              handleError,
              handleStartConversation,
              isAwaitingAiResponse,
              audioRecordingStatus,
              setAudioRecordingStatus,
            }}
          >
            <ConversationModalContent />
          </AiConversationContext.Provider>
        )}
      />
    </div>
  );
};

export default AiRolePlayLectureComponent;
